import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Copyright from '../components/Copyright';
import NavBar from '../components/NavBar';
import { useArtList } from '../contexts/ArtListProvider';

const ArtDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { artList, loading } = useArtList();
    const artId = parseInt(id!, 10);
    const artIndex = artList.findIndex((item) => item.id === artId);
    const art = artIndex !== -1 ? artList[artIndex] : null;
    const touchStartX = useRef<number>(0);

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#c9a84c' }} />
            </Box>
        );
    }

    if (!art) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h6" sx={{ color: '#9e9e9e' }}>Art not found</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <NavBar />
            <Box
                data-testid="art-detail-container"
                onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                    const delta = e.changedTouches[0].clientX - touchStartX.current;
                    if (delta < -50 && artIndex < artList.length - 1) navigate(`/art/${artList[artIndex + 1].id}`);
                    else if (delta > 50 && artIndex > 0) navigate(`/art/${artList[artIndex - 1].id}`);
                }}
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    minHeight: '100vh',
                    px: { xs: 3, md: 6 },
                    pt: { xs: 12, md: 12 },
                    pb: { xs: 4, md: 4 },
                    gap: { xs: 3, md: 6 },
                }}
            >
                {/* Image */}
                <Box
                    sx={{
                        flex: { xs: '1 1 auto', md: '0 0 60%' },
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {artIndex > 0 && (
                        <IconButton
                            onClick={() => navigate(`/art/${artList[artIndex - 1].id}`)}
                            sx={{
                                position: 'absolute',
                                left: -20,
                                color: '#9e9e9e',
                                zIndex: 2,
                                '&:hover': { color: '#f0ede8' },
                            }}
                        >
                            <ArrowBackIosNewIcon />
                        </IconButton>
                    )}
                    <img
                        src={art.image}
                        alt={art.title}
                        style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }}
                    />
                    {artIndex < artList.length - 1 && (
                        <IconButton
                            onClick={() => navigate(`/art/${artList[artIndex + 1].id}`)}
                            sx={{
                                position: 'absolute',
                                right: -20,
                                color: '#9e9e9e',
                                zIndex: 2,
                                '&:hover': { color: '#f0ede8' },
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    )}
                </Box>

                {/* Metadata */}
                <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 35%' } }}>
                    <Box
                        component="button"
                        onClick={() => navigate('/')}
                        sx={{
                            background: 'none',
                            border: 'none',
                            color: '#9e9e9e',
                            cursor: 'pointer',
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.78rem',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            p: 0,
                            mb: 4,
                            '&:hover': { color: '#f0ede8' },
                            transition: 'color 0.2s',
                        }}
                    >
                        ← Back to Gallery
                    </Box>
                    <Box sx={{ width: 32, height: 1, backgroundColor: '#c9a84c', mb: 3 }} />
                    <Typography
                        variant="h3"
                        sx={{ mb: 1, fontSize: { xs: '1.8rem', md: '2.5rem' }, lineHeight: 1.2 }}
                    >
                        {art.title}
                    </Typography>
                    {(art.medium || art.date) && (
                        <Typography
                            variant="body2"
                            sx={{ color: '#9e9e9e', mb: 3, fontFamily: '"Inter", sans-serif', fontSize: '0.8rem' }}
                        >
                            {[art.medium, art.date].filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                    {art.description && (
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#c8c4be',
                                lineHeight: 1.8,
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '0.9rem',
                            }}
                        >
                            {art.description}
                        </Typography>
                    )}
                </Box>
            </Box>
            <Copyright />
        </Box>
    );
};

export default ArtDetailPage;
